import {
  Add01Icon,
  ArrowRight02Icon,
  CoinsIcon,
  SparklesIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Link, createFileRoute } from '@tanstack/react-router';

import { useMutation, useQuery } from 'convex/react';

import { api } from '@/../convex/_generated/api';
import wcBear from '@/assets/images/wc.svg';
import { CreateBalanceSheetModal } from '@/components/CreateBalanceSheetModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { AuthenticatedRoute } from '@/lib/authenticatedRoute';

const SHEET_ICONS = ['📊', '💰', '🏦', '📈', '💵', '🐻', '🎯', '⭐️'];

function getSheetIcon(id: string) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  return SHEET_ICONS[Math.abs(hash) % SHEET_ICONS.length];
}

function BalanceSheetsPage() {
  const sheets = useQuery(api.balanceSheets.listForCurrentUser);
  const createWithSeedData = useMutation(api.balanceSheets.createWithSeedData);

  const handleCreateSampleSheet = async () => {
    try {
      await createWithSeedData({
        name: 'Sample Balance Sheet',
        displayCurrency: 'USD',
      });
    } catch (error) {
      console.error('Failed to create sample balance sheet:', error);
    }
  };

  return (
    <div className="container mx-auto pt-8 pb-12">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <img src={wcBear} alt="" className="h-10 w-10" />
          <h1 className="text-3xl font-bold">Your Balance Sheets</h1>
        </div>
        <p className="text-muted-foreground">
          Track your wealth journey, one balance sheet at a time.
        </p>
      </div>

      {!sheets && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      )}

      {sheets && sheets.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <span className="text-7xl">🌱</span>
                </EmptyMedia>
                <EmptyTitle className="text-2xl">
                  Start your wealth journey
                </EmptyTitle>
                <EmptyDescription className="text-base">
                  Create your first balance sheet to begin tracking your
                  finances. Every great portfolio starts somewhere!
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent className="flex-row flex-wrap justify-center gap-3 !max-w-none">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleCreateSampleSheet}
                >
                  <HugeiconsIcon
                    icon={SparklesIcon}
                    size={18}
                    strokeWidth={2}
                    className="mr-2"
                  />
                  Try with sample data
                </Button>
                <CreateBalanceSheetModal>
                  <Button size="lg">
                    <HugeiconsIcon
                      icon={Add01Icon}
                      size={18}
                      strokeWidth={2}
                      className="mr-2"
                    />
                    Create your first sheet
                  </Button>
                </CreateBalanceSheetModal>
              </EmptyContent>
            </Empty>
          </CardContent>
        </Card>
      )}

      {sheets && sheets.length > 0 && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sheets
              .filter(s => s !== null)
              .map(sheet => (
                <Link
                  key={sheet._id}
                  to="/balance-sheet/$id"
                  params={{ id: sheet._id }}
                  className="block group"
                >
                  <Card className="h-full transition-all duration-200 hover:shadow-md hover:border-primary/30 group-hover:scale-[1.01]">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="text-5xl">
                            {getSheetIcon(sheet._id)}
                          </span>
                          <div>
                            <h3 className="font-semibold text-xl group-hover:text-primary transition-colors">
                              {sheet.name}
                            </h3>
                            <div className="flex items-center gap-2 text-base text-muted-foreground mt-1">
                              <HugeiconsIcon
                                icon={CoinsIcon}
                                size={18}
                                strokeWidth={2}
                              />
                              <span>{sheet.displayCurrency}</span>
                            </div>
                          </div>
                        </div>
                        <div className="p-2.5 rounded-full bg-muted/50 group-hover:bg-primary/10 transition-colors">
                          <HugeiconsIcon
                            icon={ArrowRight02Icon}
                            size={20}
                            strokeWidth={2}
                            className="text-muted-foreground group-hover:text-primary transition-colors"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}

            <Card className="h-full border-dashed transition-all duration-200 hover:shadow-md hover:border-primary/30 hover:scale-[1.01]">
              <CardContent className="px-6 h-full flex gap-2">
                <CreateBalanceSheetModal>
                  <div className="flex-1 flex flex-col items-center justify-center gap-2 py-3 px-3 cursor-pointer text-muted-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors group">
                    <div className="p-2 rounded-full border-2 border-dashed border-current">
                      <HugeiconsIcon
                        icon={Add01Icon}
                        size={20}
                        strokeWidth={2}
                      />
                    </div>
                    <span className="text-sm font-medium text-center">
                      Create new
                    </span>
                  </div>
                </CreateBalanceSheetModal>
                <button
                  onClick={handleCreateSampleSheet}
                  className="flex-1 flex flex-col items-center justify-center gap-2 py-3 px-3 cursor-pointer text-muted-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors group"
                >
                  <div className="p-2.5 rounded-full border-2 border-dashed border-current">
                    <HugeiconsIcon
                      icon={SparklesIcon}
                      size={20}
                      strokeWidth={2}
                    />
                  </div>
                  <span className="text-sm font-medium text-center">
                    Try sample
                  </span>
                </button>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/balance-sheets')({
  component: AuthenticatedRoute(BalanceSheetsPage),
});
